<template>
  <base-wrapper>
    <div class="space-y-6">
      <div v-if="stats" class="grid grid-cols-7 gap-4">
        <base-widget
          v-for="[stat, value] in Object.entries(stats)"
          :key="stat"
          :label="getStatusBadge(stat).text"
          :value="value"
          class="cursor-pointer hover:bg-gray-50"
          @click="onClickWidget(stat)"
        />
      </div>
      <hr />
      <div class="flex items-center justify-between">
        <base-tab-nav :tabs="tabs" v-model="currentTab" @change="onChangeTab" />
        <div
          v-if="currentTab === 'history'"
          class="flex items-center justify-end gap-2"
        >
          <base-input
            :shadow="false"
            type="search"
            placeholder="Cari Mitra"
            debounce
            v-model="filter.search_office"
            @native-input="loadHistoryLogs"
          />
          <base-input
            :shadow="false"
            type="search"
            placeholder="Cari Telepon"
            debounce
            v-model="filter.search_phone"
            @native-input="loadHistoryLogs"
          />
          <base-select
            :shadow="false"
            :expand="false"
            :options="[
              { key: 'null', value: 'null', label: 'Semua' },
              { key: 'date', value: 'date', label: 'Per Tanggal' },
              { key: 'period', value: 'period', label: 'Per Periode' },
            ]"
            v-model="filter.filter_mode"
            @change="onChangeFilterMode"
          />
          <base-input
            v-show="filter.filter_mode === 'date'"
            :shadow="false"
            type="date"
            v-model="filter.date"
            @change="loadHistoryLogs"
          />
          <mitra-period-select
            v-show="filter.filter_mode === 'period'"
            v-model="filter.mitra_period_id"
            @change="onChangePeriod"
          />
          <base-select
            :expand="false"
            :shadow="false"
            :options="[
              { key: 'all', value: null, label: 'Status' },
              {
                key: 'dispatched',
                value: 'dispatched',
                label: 'Menunggu Antrian',
              },
              { key: 'created', value: 'created', label: 'Diproses' },
              { key: 'sent', value: 'sent', label: 'Terkirim' },
              { key: 'delivered', value: 'delivered', label: 'Diterima' },
              { key: 'read', value: 'read', label: 'Dibaca' },
              { key: 'failed', value: 'failed', label: 'Gagal' },
            ]"
            v-model="filter.status"
            @change="loadHistoryLogs"
          />
          <base-button @click="onExport" :loading="loadingExport.visible">{{
            loadingExport.visible ? loadingExport.text : 'Export'
          }}</base-button>
        </div>
      </div>

      <template>
        <datatable
          v-if="currentTab === 'history'"
          :total="historyLogs.meta.page.total"
          :perPage="historyLogs.meta.page.perPage"
          :currentPage="historyLogs.meta.page.currentPage"
          :columns="[
            { id: 'date', name: 'Tanggal' },
            { id: 'office_code', name: 'Kode Mitra' },
            { id: 'office_name', name: 'Nama Mitra' },
            { id: 'register_code', name: 'Kode Stockist' },
            { id: 'phone', name: 'No Telp' },
            { id: 'status', name: 'Status', theadClass: 'text-center' },
          ]"
          :scroll-x="false"
          @pagechanged="onChangeHistoryPage"
        >
          <template #tbody="{ classes }">
            <tr
              v-for="historyLog in historyLogs.data"
              :key="historyLog.id"
              :class="[classes.tr, 'bg-white']"
            >
              <td :class="classes.td">
                {{ historyLog.attributes.createdAt | formatDate }}
              </td>
              <td :class="classes.td">
                {{ getOfficeIncluded(historyLog).attributes.code }}
              </td>
              <td :class="classes.td">
                {{ getOfficeIncluded(historyLog).attributes.name }}
              </td>
              <td :class="classes.td">
                {{ getOfficeRegisteredCode(historyLog) }}
              </td>
              <td :class="classes.td">
                {{ historyLog.attributes.phone }}
              </td>
              <td :class="classes.td" :title="getStatusBadge(historyLog.attributes.status).text">
                <Icon :icon="getStatusBadge(historyLog.attributes.status).icon" :class="['w-5 h-5 mx-auto', getStatusBadge(historyLog.attributes.status).iconColor]" />
              </td>
            </tr>
          </template>
        </datatable>
        <datatable
          v-else
          :total="errorLogs.meta.page.total"
          :perPage="errorLogs.meta.page.perPage"
          :currentPage="errorLogs.meta.page.currentPage"
          :columns="[
            { id: 'date', name: 'Tanggal' },
            { id: 'office_code', name: 'Kode Mitra' },
            { id: 'phone', name: 'No Telp' },
            { id: 'message', name: 'Pesan' },
          ]"
          :scroll-x="false"
          @pagechanged="onChangeErrorPage"
        >
          <template #tbody="{ classes }">
            <tr
              v-for="errorLogs in errorLogs.data"
              :key="errorLogs.id"
              :class="[classes.tr, 'bg-white']"
            >
              <td :class="classes.td">
                {{ errorLogs.attributes.createdAt | formatDate }}
              </td>
              <td :class="classes.td">
                {{ errorLogs.attributes.office_code }}
              </td>
              <td :class="classes.td">
                {{ errorLogs.attributes.phone }}
              </td>
              <td :class="classes.td">
                {{ errorLogs.attributes.message }}
              </td>
            </tr>
          </template>
        </datatable>
      </template>
    </div>

    <loading v-if="loading" />
  </base-wrapper>
</template>

<script>
import { requestMixin } from '@/mixins/request/request';
import { downloadFileUrl } from '@/services/utils.service';
import { mapGetters, mapActions } from 'vuex';
import BaseWidget from '@/components/base/BaseWidget.vue';
import BaseTabNav from '@/components/base/BaseTabNav.vue';
import MitraPeriodSelect from '@/components/period/mitra-period/mitra-period-select.vue';
import { sum } from '@/utils/array';
import dayjs from 'dayjs';

export default {
  components: { BaseWidget, BaseTabNav, MitraPeriodSelect },
  mixins: [requestMixin],
  data() {
    return {
      filter: {
        date: dayjs().format('YYYY-MM-DD'),
        status: null,
        search_phone: null,
        search_office: null,
        filter_mode: 'null',
        mitra_period_id: null,
      },
      loading: false,
      loadingExport: {
        text: null,
        visible: false,
      },
      historyLogs: {
        data: [],
        meta: {
          page: {},
        },
        reload: false,
      },
      errorLogs: {
        data: [],
        meta: {
          page: {},
        },
        reload: true,
      },
      stats: {
        total: 0,
        dispatched: 0,
        created: 0,
        sent: 0,
        delivered: 0,
        read: 0,
        failed: 0,
      },
      tabs: [
        { id: 'history', name: 'Riwayat Log', href: '#' },
        { id: 'error', name: 'Error Log', href: '#' },
      ],
      currentTab: 'history',
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
      getChannelName: 'auth/getChannelName'
    }),
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    getStatusBadge(status) {
      return {
        total: {
          color: 'yellow',
          text: 'Total',
          icon: 'mdi:timer',
          iconColor: 'text-yello-600'
        },
        dispatched: {
          color: 'yellow',
          text: 'Menunggu Antrian',
          icon: 'mdi:timer',
          iconColor: 'text-yellow-600'
        },
        created: {
          color: 'indigo',
          text: 'Diproses',
          icon: 'mdi:timer-check',
          iconColor: 'text-indigo-600'
        },
        sent: {
          color: 'green',
          text: 'Terkirim',
          icon: 'mdi:check',
          iconColor: 'text-gray-600'
        },
        delivered: {
          color: 'purple',
          text: 'Diterima',
          icon: 'mdi:check-all',
          iconColor: 'text-gray-600'
        },
        read: {
          color: 'blue',
          text: 'Dibaca',
          icon: 'mdi:check-all',
          iconColor: 'text-blue-600'
        },
        failed: {
          color: 'red',
          text: 'Gagal',
          icon: 'mdi:dangerous',
          iconColor: 'text-red-600'
        },
      }[status];
    },
    getOfficeIncluded(log) {
      return this.getSingleIncluded(this.historyLogs, log.relationships.office.data.id)
    },
    getOfficeRegisteredCode(log) {
      const office = this.getOfficeIncluded(log)

      if (!office.attributes.upline_histories) {
        return '-'
      }

      const upline = office.attributes.upline_histories.find(upline => upline.type === 'registered_by')

      if (!upline) {
        return '-'
      }

      return upline.code
    },
    async loadHistoryLogs(params) {
      this.loading = true;

      const [res, err] = await this.request(
        '/api/v1/member-activation-notifications',
        {
          params: {
            ...(this.filter.filter_mode === 'null'
              ? {}
              : {
                  [this.filter.filter_mode === 'date'
                    ? 'filter[date]'
                    : 'filter[mitra_period_id]']:
                    this.filter.filter_mode === 'date'
                      ? this.filter.date
                      : this.filter.mitra_period_id,
                }),
            'filter[status]': this.filter.status,
            'filter[search_phone]': this.filter.search_phone,
            'filter[search_office]': this.filter.search_office,
            'fields[member-activation-notifications]':
              'createdAt,phone,status,office',
            'fields[offices]': 'code,name,upline_histories',
            'include': 'office',
            'page[size]': 5,
            sort: '-id',
            ...params,
          },
        }
      );

      if (!err) {
        this.historyLogs = {
          ...res,
          reload: false,
        };

        if (params?.with_stats) {
          const baseStats = {
            dispatched: 0,
            created: 0,
            sent: 0,
            delivered: 0,
            read: 0,
            failed: 0,
          };

          this.stats = {
            total: sum(this.historyLogs.meta.stats, (stat) => stat.value),
            ...baseStats,
            ...Object.fromEntries(
              this.historyLogs.meta.stats.map((stat) => [
                stat.status,
                stat.value,
              ])
            ),
          };
        }
      }

      this.loading = false;
    },
    async loadErrorLogs(params) {
      this.loading = true;

      const [res, err] = await this.request(
        '/api/v1/notify-activated-member-invoice-errors',
        {
          params: {
            'fields[notify-activated-member-invoice-errors]':
              'createdAt,office_code,phone,message',
            'page[size]': 10,
            sort: '-id',
            ...params,
          },
        }
      );

      if (!err) {
        this.errorLogs = {
          ...res,
          reload: false,
        };
      }

      this.loading = false;
    },
    async onChangeHistoryPage(page) {
      await this.loadHistoryLogs({
        'page[number]': page,
      });

      this.historyLogs.reload = true;
    },
    async onChangeErrorPage(page) {
      await this.loadErrorLogs({
        'page[number]': page,
      });

      this.errorLogs.reload = true;
    },
    onChangeFilterMode() {
      this.loadHistoryLogs({ with_stats: true });
    },
    onChangePeriod() {
      this.loadHistoryLogs({ with_stats: true });
    },
    async onChangeTab() {
      if (this.currentTab === 'history' && this.historyLogs.reload) {
        this.loadHistoryLogs();
      } else if (this.currentTab === 'error' && this.errorLogs.reload) {
        this.loadErrorLogs();
      }
    },
    onClickWidget(stat) {
      if (this.filter.filter_mode !== 'period') {
        this.filter.filter_mode = 'period';
      }

      this.filter.status = stat === 'total' ? null : stat;

      this.loadHistoryLogs();
    },
    async onExport() {
      this.startLoadingExport('Mengirim data');

      const [, error] = await this.request(
        '/api/v1/member-activation-notifications/-actions/export',
        {
          method: 'post',
          data: {
            ...(this.filter.filter_mode === 'null'
              ? {}
              : {
                  [this.filter.filter_mode === 'date'
                    ? 'date'
                    : 'mitra_period_id']:
                    this.filter.filter_mode === 'date'
                      ? this.filter.date
                      : this.filter.mitra_period_id,
                }),
            status: this.filter.status,
          },
        }
      );

      if (error) {
        this.createAlert({
          data: this.getRequestErrorMessage(error),
          status: 'error',
        });

        this.stopLoadingExport();
      }
    },
    onExportProgress(e) {
      if (
        e.type ===
        'App\\Notifications\\MemberActivationNotificationExported'
      ) {
        if (e.status === 'processing') {
          this.loadingExport.text = 'Sedang Diproses';
        } else if (e.status === 'failed') {
          this.stopLoadingExport();

          this.createAlert({
            data: 'Gagal export',
            status: 'error',
          });
        } else if (e.status === 'finished') {
          this.stopLoadingExport();

          downloadFileUrl(e.file_url, 'test.csv');
        }
      }
    },
    stopListenExportNotification() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      if (userChannel) {
        userChannel.unbind('Illuminate\\Notifications\\Events\\BroadcastNotificationCreated', this.onExportProgress);
      }
    },
    startListenExportNotification() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      userChannel.bind(
        'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
        this.onExportProgress
      );
    },
    startLoadingExport(text) {
      this.loadingExport.text = text;
      this.loadingExport.visible = true;
    },
    stopLoadingExport() {
      this.loadingExport.text = null;
      this.loadingExport.visible = false;
    },
  },
  created() {
    this.filter.mitra_period_id = this.me.current_period.id;

    this.loadHistoryLogs({
      with_stats: true,
    });
    this.startListenExportNotification();
  },
  beforeRouteLeave(to, from, next) {
    this.stopListenExportNotification();

    next();
  },
};
</script>
